import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BadgeCheck, ChevronDown, Star, Pencil, Target, Map, FileDown, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDate, cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

function fmtValue(eur) {
  if (!eur) return null;
  if (eur >= 1000000) return `${(eur / 1000000).toFixed(1).replace(".0", "")}M €`;
  if (eur >= 1000) return `${Math.round(eur / 1000)}K €`;
  return `${eur} €`;
}

function Stars({ n }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${tfr("Potentiel")} ${n || 0}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={cn("h-4 w-4", i <= (n || 0) ? "text-warning fill-current" : "text-muted-foreground/30")} />
      ))}
    </span>
  );
}

function EvalDialog({ open, onOpenChange, player, onChanged }) {
  const [potential, setPotential] = useState(player.potential || 0);
  const [value, setValue] = useState(player.market_value_eur || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/players/${player.id}`, { potential, market_value_eur: value ? Number(value) : 0 });
      toast.success(tfr("Évaluation mise à jour"));
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour l'évaluation")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="eval-dialog">
        <DialogHeader>
          <DialogTitle>{tfr("Évaluation de l'athlète")}</DialogTitle>
          <DialogDescription>{tfr("Votre estimation interne — modifiable à tout moment.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Potentiel</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onClick={() => setPotential(i === potential ? 0 : i)} data-testid={`eval-star-${i}`}
                  className="p-1 rounded hover:bg-secondary transition-colors">
                  <Star className={cn("h-6 w-6", i <= potential ? "text-warning fill-current" : "text-muted-foreground/30")} />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eval-value">{tfr("Valeur estimée (€)")}</Label>
            <Input id="eval-value" type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="Ex. 120000" data-testid="eval-value-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tfr("Annuler")}</Button>
          <Button onClick={save} disabled={saving} data-testid="eval-save-btn">{tfr("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Fact = ({ label, children, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <div className="mt-1 text-sm font-semibold">{children}</div>
  </div>
);

export function PlayerSummaryCard({ player, contract, plan, onAction, onChanged }) {
  const [evalOpen, setEvalOpen] = useState(false);
  const contractEnd = contract?.end_date || plan?.contract_end_date;
  const value = fmtValue(player.market_value_eur);
  const identity = [
    player.age ? `${player.age} ans${player.birth_date ? ` (${formatDate(player.birth_date)})` : ""}` : (player.birth_date ? formatDate(player.birth_date) : null),
    player.nationality,
    [player.level, player.club].filter(Boolean).join(" - "),
    contractEnd ? `Contrat jusqu'au ${formatDate(contractEnd)}` : null,
  ].filter(Boolean);

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="player-summary-card">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className="h-20 w-20 rounded-2xl overflow-hidden bg-secondary flex items-center justify-center shrink-0">
            {player.photo_url ? (
              <img src={player.photo_url} alt={player.name} className="h-full w-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-muted-foreground">{(player.name || "?").charAt(0)}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="text-xl font-semibold truncate" data-testid="summary-player-name">{player.name}</h2>
              {player.status === "Mandat signé" && (
                <BadgeCheck className="h-5 w-5 text-info shrink-0" aria-label={tfr("Sous mandat")} />
              )}
            </div>
            {player.position && <p className="text-sm text-athlynx font-medium mt-0.5">{player.position}</p>}
            <p className="text-xs text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {identity.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/50" />}{t}
                </span>
              ))}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 lg:pl-6 lg:border-l border-border shrink-0">
          <Fact label="Niveau actuel" testid="summary-level">{player.level || "—"}</Fact>
          <Fact label="Potentiel" testid="summary-potential">
            <button onClick={() => setEvalOpen(true)} className="hover:opacity-80 transition-opacity" data-testid="summary-potential-btn">
              {player.potential ? <Stars n={player.potential} /> : <span className="text-muted-foreground text-xs inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> {tfr("Évaluer")}</span>}
            </button>
          </Fact>
          <Fact label={tfr("Valeur estimée")} testid="summary-value">
            <button onClick={() => setEvalOpen(true)} className="hover:opacity-80 transition-opacity" data-testid="summary-value-btn">
              {value || <span className="text-muted-foreground text-xs inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Estimer</span>}
            </button>
          </Fact>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-9 gap-1.5" data-testid="summary-actions-btn">
                Actions <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAction("goal")} data-testid="action-add-goal">
                <Target className="h-3.5 w-3.5 mr-2" /> Ajouter un objectif
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction("milestone")} data-testid="action-add-milestone">
                <Map className="h-3.5 w-3.5 mr-2" /> Ajouter un jalon
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction("meeting")} data-testid="action-meeting">
                <CalendarClock className="h-3.5 w-3.5 mr-2" /> Programmer une réunion
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEvalOpen(true)} data-testid="action-eval">
                <Star className="h-3.5 w-3.5 mr-2" /> Modifier l'évaluation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction("report")} data-testid="action-report">
                <FileDown className="h-3.5 w-3.5 mr-2" /> Générer un rapport
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <EvalDialog key={`${player.potential || 0}-${player.market_value_eur || 0}`}
        open={evalOpen} onOpenChange={setEvalOpen} player={player} onChanged={onChanged} />
    </section>
  );
}
